import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config/apiConfig';

function formatTime(value) {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat('es-UY', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
}

function ChatAdmin() {
  const [token, setToken] = useState(() => window.localStorage.getItem('babyplanner_admin_token') || '');
  const [tokenInput, setTokenInput] = useState(token);
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [adminName, setAdminName] = useState('Admin');
  const [activeTab, setActiveTab] = useState('chats');
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactActionId, setContactActionId] = useState('');
  const [chatActionId, setChatActionId] = useState('');

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const selectedSummary = chats.find((chat) => chat.id === selectedChatId);

  const loadChats = async () => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats`, {
        headers: authHeaders
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudieron cargar los chats.');
      }

      setChats(data.chats || []);
      setAdminName(data.adminName || 'Admin');
      setError('');

      if (!selectedChatId && data.chats?.[0]) {
        setSelectedChatId(data.chats[0].id);
      }
    } catch (loadError) {
      setError(loadError.message);
    }
  };

  const updateChatStatus = async (sessionId, updates) => {
    setChatActionId(sessionId);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${sessionId}`, {
        method: 'PATCH',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo actualizar la conversación.');
      }

      setSelectedChat(data.chat);
      setChats((current) => current.map((chat) => (
        chat.id === sessionId ? data.chat : chat
      )));
      setError('');
      await loadChats();
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setChatActionId('');
    }
  };

  const deleteChat = async (chat) => {
    const confirmed = window.confirm(
      '¿Eliminar definitivamente esta conversación? Esta acción no se puede deshacer.'
    );
    if (!confirmed) {
      return;
    }

    setChatActionId(chat.id);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${chat.id}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo eliminar la conversación.');
      }

      setChats((current) => current.filter((item) => item.id !== chat.id));
      setSelectedChat(null);
      setSelectedChatId('');
      setError('');
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setChatActionId('');
    }
  };

  const loadContacts = async () => {
    if (!token) {
      return;
    }

    setContactsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/contacts`, {
        headers: authHeaders
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudieron cargar los contactos.');
      }

      setContacts(data.contacts || []);
      setError('');
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setContactsLoading(false);
    }
  };

  const updateContactStatus = async (contactId, updates) => {
    setContactActionId(contactId);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/contacts/${contactId}`, {
        method: 'PATCH',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo actualizar el contacto.');
      }

      setContacts((current) => current.map((contact) => (
        contact.id === contactId ? data.contact : contact
      )));
      setError('');
      await loadContacts();
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setContactActionId('');
    }
  };

  const deleteContact = async (contact) => {
    const confirmed = window.confirm(
      `¿Eliminar definitivamente el mensaje de ${contact.name}? Esta acción no se puede deshacer.`
    );
    if (!confirmed) {
      return;
    }

    setContactActionId(contact.id);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/contacts/${contact.id}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo eliminar el contacto.');
      }

      setContacts((current) => current.filter((item) => item.id !== contact.id));
      setError('');
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setContactActionId('');
    }
  };

  const loadSelectedChat = async () => {
    if (!token || !selectedChatId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${selectedChatId}`, {
        headers: authHeaders
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo cargar la conversación.');
      }

      setSelectedChat(data.chat);
      setAdminName(data.chat?.adminName || adminName);
      setError('');
    } catch (loadError) {
      setError(loadError.message);
    }
  };

  useEffect(() => {
    if (activeTab !== 'chats') {
      return undefined;
    }

    loadChats();
    const interval = window.setInterval(loadChats, 4000);

    return () => window.clearInterval(interval);
  }, [token, selectedChatId, activeTab]);

  useEffect(() => {
    if (activeTab !== 'chats') {
      return undefined;
    }

    loadSelectedChat();
    const interval = window.setInterval(loadSelectedChat, 2500);

    return () => window.clearInterval(interval);
  }, [token, selectedChatId, activeTab]);

  useEffect(() => {
    if (activeTab !== 'contacts') {
      return undefined;
    }

    loadContacts();
    const interval = window.setInterval(loadContacts, 10000);

    return () => window.clearInterval(interval);
  }, [token, activeTab]);

  const handleLogin = (event) => {
    event.preventDefault();
    const trimmed = tokenInput.trim();
    setToken(trimmed);
    window.localStorage.setItem('babyplanner_admin_token', trimmed);
  };

  const handleLogout = () => {
    setToken('');
    setTokenInput('');
    setChats([]);
    setSelectedChat(null);
    setSelectedChatId('');
    setContacts([]);
    setActiveTab('chats');
    window.localStorage.removeItem('babyplanner_admin_token');
  };

  const sendReply = async (event) => {
    event.preventDefault();
    const trimmed = reply.trim();

    if (!trimmed || !selectedChatId) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${selectedChatId}/reply`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: trimmed })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo responder.');
      }

      setSelectedChat(data.chat);
      setReply('');
      loadChats();
      setError('');
    } catch (sendError) {
      setError(sendError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const setAdminMode = async (adminActive) => {
    if (!selectedChatId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${selectedChatId}/mode`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ adminActive })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo cambiar el modo.');
      }

      setSelectedChat(data.chat);
      loadChats();
      setError('');
    } catch (modeError) {
      setError(modeError.message);
    }
  };

  if (!token) {
    return (
      <main className="page-shell admin-page">
        <section className="admin-login-card">
          <p className="eyebrow">Chat admin</p>
          <h2>Ingresar al panel</h2>
          <p className="section-lede">Usa el token definido en <code>ADMIN_CHAT_TOKEN</code> en el env del backend.</p>
          <form className="form-grid" onSubmit={handleLogin}>
            <div className="form-field">
              <label htmlFor="admin-token">Token</label>
              <input
                id="admin-token"
                type="password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <button type="submit" className="button button-primary">Entrar</button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="page-shell admin-page">
      <section className="admin-shell">
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Chat admin</p>
            <h2>{activeTab === 'chats' ? 'Conversaciones' : 'Contactos'}</h2>
          </div>
          <div className="site-admin-top-actions"><a className="button button-secondary" href="/#/site-admin">Contenido del sitio</a><button type="button" className="button button-secondary" onClick={handleLogout}>Salir</button></div>
        </header>


        <nav className="admin-tabs" aria-label="Secciones del panel">
          <button
            type="button"
            className={activeTab === 'chats' ? 'is-active' : ''}
            onClick={() => setActiveTab('chats')}
          >
            Conversaciones
            {chats.length > 0 && <span>{chats.length}</span>}
          </button>
          <button
            type="button"
            className={activeTab === 'contacts' ? 'is-active' : ''}
            onClick={() => setActiveTab('contacts')}
          >
            Contactos
            {contacts.length > 0 && <span>{contacts.length}</span>}
          </button>
        </nav>

        {error && <p className="admin-error">{error}</p>}

        {activeTab === 'chats' && (
        <div className="admin-layout">
          <aside className="admin-chat-list" aria-label="Lista de chats">
            {chats.length === 0 && <p className="admin-empty">Todavía no hay chats.</p>}
            {chats.map((chat) => (
              <button
                type="button"
                className={`admin-chat-item ${chat.id === selectedChatId ? 'is-selected' : ''} ${chat.isRead ? '' : 'is-unread'} ${chat.isStarred ? 'is-starred' : ''}`}
                key={chat.id}
                onClick={() => setSelectedChatId(chat.id)}
              >
                <strong>{chat.id.slice(0, 18)}</strong>
                <span>{chat.lastMessage?.text || 'Sin mensajes'}</span>
                <small>{formatTime(chat.updatedAt)}</small>
                {chat.isStarred && <b className="admin-chat-star">Destacado</b>}
                {!chat.isRead && <em>{chat.unreadUserMessages || 1}</em>}
              </button>
            ))}
          </aside>

          <section className="admin-chat-detail">
            {!selectedChat && <p className="admin-empty">Selecciona un chat para verlo.</p>}
            {selectedChat && (
              <>
                <div className="admin-chat-toolbar">
                  <div>
                    <strong>{selectedSummary?.id || selectedChat.id}</strong>
                    <span>{selectedChat.adminActive ? 'Modo admin activo' : 'Responde el LLM'}</span>
                  </div>
                  <div className="admin-chat-toolbar-actions">
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={() => setAdminMode(!selectedChat.adminActive)}
                    >
                      {selectedChat.adminActive ? 'Devolver al LLM' : 'Tomar chat'}
                    </button>
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={() => updateChatStatus(selectedChat.id, { isRead: !selectedChat.isRead })}
                      disabled={chatActionId === selectedChat.id}
                    >
                      {selectedChat.isRead ? 'Marcar no leído' : 'Marcar leído'}
                    </button>
                    <button
                      type="button"
                      className={`button button-secondary ${selectedChat.isStarred ? 'is-active' : ''}`}
                      onClick={() => updateChatStatus(selectedChat.id, { isStarred: !selectedChat.isStarred })}
                      disabled={chatActionId === selectedChat.id}
                      aria-pressed={selectedChat.isStarred}
                    >
                      {selectedChat.isStarred ? 'Quitar destacado' : 'Destacar'}
                    </button>
                    <button
                      type="button"
                      className="button admin-danger-button"
                      onClick={() => deleteChat(selectedChat)}
                      disabled={chatActionId === selectedChat.id}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                <div className="admin-messages">
                  {selectedChat.messages.map((message) => (
                    <div className={`admin-message ${message.sender}`} key={message.id}>
                      <span>{message.sender === 'user' ? 'Usuario' : message.sender === 'admin' ? (message.displayName || adminName) : 'LLM'}</span>
                      <p>{message.text}</p>
                      <small>{formatTime(message.createdAt)}</small>
                    </div>
                  ))}
                </div>

                <form className="admin-reply-form" onSubmit={sendReply}>
                  <textarea
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder="Escribe una respuesta como admin..."
                    rows="4"
                  />
                  <button type="submit" className="button button-primary" disabled={isLoading || !reply.trim()}>
                    {isLoading ? 'Enviando...' : 'Responder como ' + adminName}
                  </button>
                </form>
              </>
            )}
          </section>
        </div>
        )}

        {activeTab === 'contacts' && (
          <section className="admin-contacts" aria-label="Contactos recibidos">
            <header className="admin-contacts-header">
              <div>
                <strong>{contacts.length} {contacts.length === 1 ? 'contacto' : 'contactos'}</strong>
                <span>Mensajes enviados desde el formulario del sitio.</span>
              </div>
              <button
                type="button"
                className="button button-secondary"
                onClick={loadContacts}
                disabled={contactsLoading}
              >
                {contactsLoading ? 'Actualizando…' : 'Actualizar'}
              </button>
            </header>

            {contacts.length === 0 && !contactsLoading && (
              <p className="admin-empty">Todavía no hay contactos enviados desde el formulario.</p>
            )}

            <div className="admin-contact-grid">
              {contacts.map((contact) => (
                <article
                  className={`admin-contact-card ${contact.isRead ? '' : 'is-unread'} ${contact.isStarred ? 'is-starred' : ''}`}
                  key={contact.id}
                >
                  <header>
                    <div>
                      <h3>{contact.name}</h3>
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </div>
                    <div className="admin-contact-badges">
                      {contact.isStarred && <span className="contact-starred">Destacado</span>}
                      {!contact.isRead && <span className="contact-unread">Nuevo</span>}
                      <span className={contact.emailSent ? 'email-sent' : 'email-pending'}>
                        {contact.emailSent ? 'Correo enviado' : 'Guardado'}
                      </span>
                    </div>
                  </header>
                  <p>{contact.message}</p>
                  <footer>
                    <time dateTime={contact.createdAt}>{formatTime(contact.createdAt)}</time>
                    <div className="admin-contact-actions">
                      <button
                        type="button"
                        onClick={() => updateContactStatus(contact.id, { isRead: !contact.isRead })}
                        disabled={contactActionId === contact.id}
                      >
                        {contact.isRead ? 'Marcar no leído' : 'Marcar leído'}
                      </button>
                      <button
                        type="button"
                        className={contact.isStarred ? 'is-active' : ''}
                        onClick={() => updateContactStatus(contact.id, { isStarred: !contact.isStarred })}
                        disabled={contactActionId === contact.id}
                        aria-pressed={contact.isStarred}
                      >
                        {contact.isStarred ? 'Quitar destacado' : 'Destacar'}
                      </button>
                      <a href={`mailto:${contact.email}?subject=Respuesta de Babyplanner Vero`}>
                        Responder por email
                      </a>
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => deleteContact(contact)}
                        disabled={contactActionId === contact.id}
                      >
                        Eliminar
                      </button>
                    </div>
                  </footer>
                </article>
              ))}
            </div>
          </section>
        )}
      </section>
    </main>
  );
}

export default ChatAdmin;
